/**
 * A moldura: barra lateral, barra superior e a área de conteúdo.
 *
 * Montada UMA vez por sessão e reaproveitada a cada troca de tela — só o miolo do
 * `<main>` e a trilha do topo são redesenhados. Isso mantém o estado do salvamento e a
 * assinatura da conexão vivos entre navegações, que é justamente o que uma casca faz.
 */
import { h, hora, limpar, poe } from './dom';
import { icone } from './icones';
import { alternarTema, temaEscuro } from './ui/tema';
import { aplicarIdsDoPerfil, definirIds, idsVisiveis, marcarIds } from './ui/ids';
import { avisar, confirmar } from './ui/aviso';
import { conexao } from '../../../packages/dados/src/conexao';
import type { EstadoConexao } from '../../../packages/dados/src/conexao';
import { sair } from '../../../packages/dados/src/sessao';
import { listarPendentesExclusao } from '../../../packages/dados/src/repositorios';
import { criarRecontagem, rotuloDoItem, SONDA_PEDIDOS_MS, textoDoContador } from './fila-pedidos';
import type { Perfil } from '../../../packages/dados/src/sessao';
import { ir } from './rotas';
import type { Rota } from './rotas';

export interface Migalha {
  texto: string;
  /** Sem rota = é o item atual da trilha, e vai em negrito. */
  rota?: Rota;
  /**
   * Texto comum: nem link, nem o item atual. É o empreendimento dentro do cenário desde
   * 09/10/2026 — a tela que ele abria (a lista de cenários) deixou de existir, e um link que
   * só reabre o cenário padrão confundiria; trocar de cenário é no seletor do título.
   */
  simples?: boolean;
}

/**
 * Um item da navegação lateral. Os `id` NÃO são decorativos: o `trocarView` do simulador
 * legado marca o item ativo procurando `#tab-<área>`. Dando a ele os mesmos ids da v67, o
 * realce e o contador de cenários voltam a funcionar sem tocar em uma linha do legado.
 */
export interface ItemNav {
  tipo: 'grupo' | 'parent' | 'sub';
  rotulo: string;
  /** `tab-sim`, `tab-flc`, … — o que o `trocarView` procura. */
  id?: string;
  /** EL-xxx, para a etiqueta. */
  el?: string;
  icone?: string;
  aoClicar?: () => void;
  /** Vira `<span class="cnt" id="cmp-count">`, que o `renderContador` preenche. */
  idContador?: string;
}

export interface Vista {
  trilha: Migalha[];
  corpo: HTMLElement;
  /** Item da barra lateral a marcar como ativo. */
  ativo?: 'incorporadoras' | 'empreendimentos' | 'pedidos' | 'documentos';
  /**
   * A incorporadora de que esta tela está falando, quando ela sabe.
   *
   * A casca guarda a última informada e é ela que o item "Documentos" usa para saber de
   * quem são as pastas — inclusive lá de dentro de um cenário, onde a barra lateral não é
   * redesenhada. Sem isto o item teria de perguntar a incorporadora toda vez, mesmo com o
   * contexto na tela.
   */
  empresaId?: string;
  /**
   * Chamada ao trocar de tela. Só quem pendurou coisa FORA do próprio corpo precisa
   * disto — o corpo é descartado sozinho. Hoje quem usa é o cenário: o simulador legado
   * põe os modais e popovers direto no `<body>`, e sem isto eles se acumulariam a cada
   * cenário aberto, com ids repetidos e `getElementById` devolvendo o mais antigo.
   */
  aoSair?: () => void;
  /**
   * Chamada quando a vista é ADOTADA — depois de o corpo entrar no documento (09/10/2026,
   * revisão adversarial do lote). É onde a tela LIGA o que tem efeito fora do próprio corpo
   * (montagem do simulador, sonda, ouvinte no documento, sessão de rascunho).
   *
   * Por que não na construção: `desenhar()` pode construir duas vistas para a mesma rota (o
   * F5 desenha duas vezes — a chamada do arranque e o INITIAL_SESSION da autenticação; o
   * "Desfazer" que reabre; trocar e voltar rápido), e a perdedora é DESCARTADA sem passar
   * por aqui nem pelo `aoSair`. O que ela tivesse ligado na construção ficava vivo para sempre:
   * uma sonda órfã que anunciava "outra pessoa publicou" sobre a própria publicação de quem
   * estava na tela, e que podia gravar o cenário de uma vista na sessão de outra. Chamar o
   * `aoSair` da descartada não serviria: ele desmonta o simulador GLOBAL, que é o da viva.
   */
  aoMostrar?: () => void;
  /**
   * Itens que a tela acrescenta à barra lateral. Hoje quem usa é o cenário: as áreas do
   * simulador (Guiado, Análise de Demanda, Análise de Cenários, Acompanhamento,
   * Premissas macro) pertencem ao cenário aberto, não ao aplicativo — mostrá-las quando
   * não há cenário levaria a telas que não sabem sobre o que falar.
   * Desde 09/10/2026, quem não é Admin Hype recebe só Análise de Demanda e Análise de
   * Cenários — o filtro é da tela (`areas()` em `telas/cenario.ts`), não daqui.
   */
  navegacao?: ItemNav[];
  /**
   * O "Carregando…" que `desenhar()` põe enquanto a vista de verdade é montada. A casca
   * não reconta os pedidos por ele — a vista de verdade vem logo depois e reconta; contar
   * nos dois dobraria as consultas a cada navegação.
   */
  provisoria?: boolean;
}

export interface Casca {
  raiz: HTMLElement;
  definirVista(v: Vista): void;
  destruir(): void;
}

export function criarCasca(perfil: Perfil): Casca {
  /* ---------- barra lateral ---------- */
  const navIncorporadoras = h(
    'button',
    { class: 'parent', onclick: () => ir({ nome: 'incorporadoras' }) },
    h('span', { html: icone.empresa(17) }),
    h('span', { class: 'lb2' }, 'Incorporadoras'),
  );

  /* Documentos: uma área do aplicativo, e não uma aba dentro do empreendimento (pedido do
     usuário em 30/07/2026). Fica entre os itens FIXOS de propósito — é o que faz o caminho
     continuar visível lá dentro do cenário, onde a barra ganha as áreas do simulador.
     `empresaCorrente` pode ser null logo depois de entrar; a rota prevê esse caso e a tela
     resolve, em vez de o item virar um botão que às vezes não funciona. */
  let empresaCorrente: string | null = null;
  const navDocumentos = h(
    'button',
    {
      class: 'parent',
      'data-el': 'EL-188',
      onclick: () => ir({ nome: 'documentos', empresaId: empresaCorrente }),
    },
    h('span', { html: icone.pasta(17) }),
    h('span', { class: 'lb2' }, 'Documentos'),
  );

  /* A fila de pedidos de exclusão é da plataforma. Nasce só para quem decide: para os
     demais, o item levaria a uma tela que a RPC recusa — e recusa DEPOIS do clique.
     O contador aparece quando há pedido esperando, e some quando não há. */
  const contadorPedidos = h('span', { class: 'cnt', style: 'display:none' });
  const navPedidos = perfil.isAdminHype
    ? h(
        'button',
        {
          class: 'parent',
          'data-el': 'EL-185',
          title: rotuloDoItem(0),
          'aria-label': rotuloDoItem(0),
          onclick: () => ir({ nome: 'pedidos' }),
        },
        h('span', { html: icone.alerta(17) }),
        h('span', { class: 'lb2' }, 'Pedidos'),
        contadorPedidos,
      )
    : null;

  /* PEDIDOS AGUARDANDO DECISÃO (lote de 09/10/2026, item 6). Antes era UMA leitura, na
     montagem — e a casca vive a sessão inteira: pedido que chegava com o admin logado só
     aparecia no F5, e depois de Aprovar/Recusar o número velho ficava lá. Agora reconta:
       · na montagem;
       · a cada vista definida (`definirVista`) — cobre navegar, o `recarregar()` que a
         tela de Pedidos chama depois de decidir/restaurar, e a troca de autenticação;
       · a cada `SONDA_PEDIDOS_MS` com a aba visível, e na hora em que a aba volta.
     A coordenação (trava de reentrada, repetir em vez de descartar, falha não apaga) mora
     em `fila-pedidos.ts`, que é puro e testado. A tela de Pedidos aberta NÃO se redesenha
     sozinha: um cartão novo entrando no topo empurraria os outros para baixo do cursor, e
     "Aprovar exclusão" arquiva uma incorporadora inteira. Só o número muda.
     Para quem não é Admin Hype nada disto existe — nem o relógio, nem a consulta. */
  const recontagem =
    navPedidos === null
      ? null
      : criarRecontagem(listarPendentesExclusao, (r) => {
          const tem = r.total > 0;
          contadorPedidos.textContent = textoDoContador(r.total);
          contadorPedidos.style.display = tem ? '' : 'none';
          contadorPedidos.classList.toggle('alerta', tem);
          /* o ponto do trilho estreito (≤900px), onde rótulo e número somem */
          navPedidos.classList.toggle('tem-pendente', tem);
          const rotulo = rotuloDoItem(r.total);
          navPedidos.title = rotulo;
          navPedidos.setAttribute('aria-label', rotulo);
        });
  /* Função NOMEADA: o ouvinte vai embora no `destruir()`. A casca é recriada no logout e na
     troca de perfil; um ouvinte anônimo deixaria uma sonda por casca morta. */
  function sondarPedidos(): void {
    if (document.visibilityState === 'hidden') return;
    void recontagem?.pedir();
  }
  const relogioPedidos = recontagem === null ? null : window.setInterval(sondarPedidos, SONDA_PEDIDOS_MS);
  if (recontagem !== null) {
    document.addEventListener('visibilitychange', sondarPedidos);
    void recontagem.pedir();
  }

  const btnTema = h('button', {
    class: 'iconbtn',
    title: 'Alternar tema',
    html: temaEscuro() ? icone.sol(15) : icone.lua(15),
    onclick: () => {
      const escuro = alternarTema();
      btnTema.innerHTML = escuro ? icone.sol(15) : icone.lua(15);
    },
  });

  const btnSair = h('button', {
    class: 'iconbtn',
    title: 'Sair do Fincast',
    html: icone.sair(15),
    onclick: () => {
      void (async () => {
        const ok = await confirmar({
          titulo: 'Sair do Fincast',
          corpo: 'Você vai precisar entrar de novo com e-mail e senha. O trabalho já salvo continua no servidor.',
          confirmar: 'Sair',
        });
        if (!ok) return;
        await sair();
      })();
    },
  });

  /* Só o Admin Hype: é ele quem conversa o produto por ID — o interruptor E o efeito. A
     preferência gravada é do navegador, não da pessoa: sem isto, quem entrasse depois do
     admin na mesma máquina via as etiquetas. ANTES do interruptor, que lê o `<body>`. */
  aplicarIdsDoPerfil(perfil.isAdminHype);
  const interruptorIds = perfil.isAdminHype
    ? h('label', { class: 'row', style: 'cursor:pointer;gap:8px', title: 'Mostra a etiqueta EL-xxx de cada elemento' },
        h('span', {}, 'Mostrar IDs'),
        h('input', {
          type: 'checkbox',
          checked: idsVisiveis(),
          onchange: (ev: Event) => {
            definirIds((ev.target as HTMLInputElement).checked);
            /* Marcar de novo: a vista atual pode ter nascido antes de o interruptor
               existir, e sem isto ela ficaria sem etiqueta até a próxima navegação. */
            marcarIds(document.body);
          },
        }))
    : null;

  /* Preenchido a cada vista. Fica FORA do `miolo` de propósito: a barra lateral não é
     redesenhada entre telas, e é isso que mantém a assinatura da conexão viva. */
  const navDaTela = h('div', {});

  /* O que é exclusivo do Admin Hype some por CSS, a partir desta classe. É o mesmo
     mecanismo do `body.ids`, e existe porque a marcação do simulador (`conteudo.html`)
     é GERADA a partir da v67 congelada — editá-la à mão tiraria o sentido do diff que
     documenta o porte. Esconder não é permissão: quem decide é o banco (RLS).
     O que some por esta classe (estilo.css): o otimizador e, desde 09/10/2026, as abas
     "Fluxo" e "Fluxo mensal". O resto do que é só do admin nasce por código — as áreas da
     barra lateral (`areas()` em telas/cenario.ts) e, no legado, a coluna "Sugestão" e as
     portas de entrada da vista (`ehAdminHype()`). */
  document.body.classList.toggle('admin-hype', perfil.isAdminHype);

  const lateral = h(
    'aside',
    { class: 'side' },
    h('div', { class: 'brand' }, h('span', { class: 'logo' }, 'F'), h('span', {}, 'Fincast')),
    h('nav', { class: 'snav' }, h('div', { class: 'grp' }, 'Navegação'), navIncorporadoras,
      navDocumentos, ...(navPedidos === null ? [] : [navPedidos]), navDaTela),
    h(
      'div',
      { class: 'side-foot' },
      /* `sf-nome` e `sf-selo` somem quando a barra encolhe (regra no estilo.css): num
         trilho de 60px o nome empurraria os botões para fora do campo de visão. */
      h(
        'div',
        { class: 'row' },
        h('span', { class: 'sf-nome', title: perfil.email }, perfil.nome),
        h('span', { class: 'sf-acts' }, btnTema, btnSair),
      ),
      interruptorIds,
      /* O nome VISÍVEL do papel é "Admin Fincast" (12/10/2026); por dentro continua
         `isAdminHype` / `is_admin_hype` / `body.admin-hype` (tests/rotulo-admin.test.ts). */
      perfil.isAdminHype
        ? h('div', { class: 'row sf-selo' }, h('span', { class: 'badge out' }, 'Admin Fincast'))
        : null,
    ),
  );

  /* ---------- barra superior ---------- */
  const trilha = h('div', { class: 'tb-crumb' });
  const salvamento = h(
    'span',
    { class: 'tb-save', title: 'Estado do salvamento automático' },
    h('span', { class: 'dot' }),
    h('span', {}, 'Pronto'),
  );

  /* 07/08/2026 — a saída que faltava. Um erro que repetir não resolve (permissão negada,
     conflito de versão) fazia o monitor girar de 5 s a 60 s para sempre, em silêncio: a
     única evidência era o rótulo mudando na barra. Agora o monitor desiste depois de três
     e a decisão passa a ser do usuário, com um botão que existe só nessa hora. */
  const tentarDeNovo = h(
    'button',
    {
      class: 'btn out sm tb-retry',
      style: 'display:none',
      onclick: () => void conexao.sincronizarAgora(true),
    },
    'Tentar de novo',
  );

  const topo = h(
    'header',
    { class: 'top', 'data-el': 'EL-168' },
    trilha,
    h('div', { class: 'tb-right' }, salvamento, tentarDeNovo),
  );

  /* A barra de estado é o único ponto onde a queda de conexão aparece sozinha, sem o
     usuário ter feito nada. Por isso assina o monitor em vez de ser atualizada por quem
     salva: quem salva pode nem estar na tela quando a rede cai. */
  function pintarConexao(e: EstadoConexao): void {
    const rotulo =
      e.rede === 'offline' ? 'Sem conexão'
        : e.salvamento === 'salvando' ? 'Salvando…'
        : e.salvamento === 'pendente' ? `${e.pendencias} alteração(ões) esperando`
        : e.salvamento === 'erro' ? 'Não foi possível salvar'
        : e.salvoEm !== null ? `Salvo às ${hora(e.salvoEm)}`
        : 'Pronto';
    salvamento.className =
      'tb-save' + (e.salvamento === 'salvando' ? ' saving' : e.salvamento === 'salvo' ? ' saved' : '');
    salvamento.lastElementChild!.textContent = rotulo;
    salvamento.title = e.mensagem;
    tentarDeNovo.style.display = e.salvamento === 'erro' ? '' : 'none';
  }
  const desassinar = conexao.assinar(pintarConexao);

  /* ---------- conteúdo ---------- */
  const miolo = h('main', {});
  const raiz = h('div', { class: 'app' }, lateral, h('div', { class: 'content' }, topo, miolo));

  let sairDaVistaAtual: (() => void) | null = null;

  function definirVista(v: Vista): void {
    /* Antes de qualquer coisa: deixar a vista anterior recolher o que ela pendurou fora
       do próprio corpo. Se isto vier depois de montar a nova, os dois convivem por um
       instante e a limpeza leva junto o que acabou de ser criado. */
    try {
      sairDaVistaAtual?.();
    } catch {
      /* uma limpeza que falha não pode impedir a navegação */
    }
    sairDaVistaAtual = v.aoSair ?? null;

    limpar(trilha);
    const pecas: Node[] = [];
    v.trilha.forEach((m, i) => {
      if (i > 0) pecas.push(h('span', { class: 'sep' }, '/'));
      pecas.push(
        m.simples === true
          ? h('span', {}, m.texto)
          : m.rota === undefined
          ? h('b', {}, m.texto)
          : h('a', {
              class: 'lnk',
              href: '#',
              onclick: (e: Event) => {
                e.preventDefault();
                ir(m.rota!);
              },
            }, m.texto),
      );
    });
    poe(trilha, pecas);

    /* A tela que sabe de qual incorporadora fala deixa isso registrado; a que não sabe
       (a lista de incorporadoras, a fila de pedidos) NÃO apaga o que já havia — voltar
       para a raiz não deve fazer o item de Documentos esquecer onde a pessoa estava. */
    if (v.empresaId !== undefined) empresaCorrente = v.empresaId;

    navIncorporadoras.classList.toggle('active', v.ativo === 'incorporadoras');
    navDocumentos.classList.toggle('active', v.ativo === 'documentos');
    navPedidos?.classList.toggle('active', v.ativo === 'pedidos');
    /* Recontar a cada vista é o que faz o número DIMINUIR depois de Aprovar/Recusar/
       Restaurar: a tela de Pedidos chama `recarregar()`, que passa por aqui. */
    if (v.provisoria !== true) void recontagem?.pedir();

    limpar(navDaTela);
    for (const item of v.navegacao ?? []) {
      if (item.tipo === 'grupo') {
        navDaTela.appendChild(h('div', { class: 'grp' }, item.rotulo));
        continue;
      }
      const b = h('button', {
        class: item.tipo,
        id: item.id,
        'data-el': item.el,
        onclick: item.aoClicar,
      });
      if (item.icone !== undefined) b.appendChild(h('span', { html: item.icone }));
      b.appendChild(h('span', { class: 'lb2' }, item.rotulo));
      if (item.idContador !== undefined) {
        b.appendChild(h('span', { class: 'cnt', id: item.idContador, style: 'display:none' }));
      }
      navDaTela.appendChild(b);
    }
    limpar(miolo);
    miolo.appendChild(v.corpo);
    /* Depois de o corpo entrar no documento: as telas declaram `data-el` e é aqui que
       ele vira etiqueta visível. Feito sempre, e não só com o interruptor ligado — a
       etiqueta nasce escondida por CSS, então ligar o interruptor é instantâneo. */
    if (perfil.isAdminHype) { marcarIds(miolo); marcarIds(navDaTela); }
    miolo.scrollTo?.({ top: 0 });
    /* Só agora a vista é a da tela: o que ela liga daqui em diante o `aoSair` dela desliga. */
    try {
      v.aoMostrar?.();
    } catch (e) {
      console.error('[Fincast] a tela não conseguiu ligar o que precisava:', e);
    }
  }

  function destruir(): void {
    desassinar();
    /* A sonda dos pedidos é da casca, não de uma vista: some aqui, e não num `aoSair`. */
    recontagem?.parar();
    if (relogioPedidos !== null) window.clearInterval(relogioPedidos);
    document.removeEventListener('visibilitychange', sondarPedidos);
    raiz.remove();
  }

  return { raiz, definirVista, destruir };
}

/* ============================================================================
 * Peças reusadas pelas telas
 * ========================================================================== */

export function cabecalhoDeTela(titulo: string, descricao: string, ...acoes: (Node | false | null)[]): HTMLElement {
  return h(
    'div',
    { class: 'pagehead' },
    h('div', {}, h('h2', {}, titulo), h('p', { class: 'desc' }, descricao)),
    h('div', { class: 'tbar-r' }, ...acoes),
  );
}

export function carregando(o_que: string): HTMLElement {
  return h('div', { class: 'card', style: 'padding:28px;text-align:center' },
    h('span', { class: 'muted' }, `Carregando ${o_que}…`));
}

export function vazio(mensagem: string, ...acoes: (Node | false | null)[]): HTMLElement {
  return h(
    'div',
    { class: 'card', style: 'padding:40px 28px;text-align:center;display:grid;gap:14px;justify-items:center' },
    h('span', { class: 'muted', style: 'opacity:.5', html: icone.predio(30) }),
    h('span', { class: 'muted' }, mensagem),
    acoes.length > 0 ? h('div', { style: 'display:flex;gap:8px' }, ...acoes) : null,
  );
}

/**
 * Erro na tela inteira. Mostra a frase que a camada de dados já traduziu — nunca o texto
 * cru do Postgres, que fala de política e de coluna.
 */
export function falha(e: unknown, tentarDeNovo: () => void): HTMLElement {
  const msg = e instanceof Error ? e.message : String(e);
  return h(
    'div',
    { class: 'card', style: 'padding:28px;display:grid;gap:14px;justify-items:center;text-align:center' },
    h('span', { style: 'color:hsl(var(--destructive))', html: icone.alerta(26) }),
    h('span', {}, msg),
    h('button', { class: 'btn out', onclick: tentarDeNovo }, 'Tentar de novo'),
  );
}

/** Envolve uma ação de botão: mostra o erro traduzido no toast em vez de sumir calado. */
export async function tentar<T>(operacao: () => Promise<T>, aoDarCerto?: (v: T) => void): Promise<void> {
  try {
    const v = await operacao();
    aoDarCerto?.(v);
  } catch (e) {
    avisar(e instanceof Error ? e.message : String(e), 'erro');
  }
}
